import {
  authReturnWithoutHash,
  isCommunioApexHost,
  isCommunioProductSlugHost,
  normalizeOpsAuthReturn,
} from './authDomain';

const ID_TOKEN_KEY = 'communio_ops_id_token';
const REFRESH_TOKEN_KEY = 'communio_ops_refresh_token';
const UID_KEY = 'communio_ops_uid';
export const ID_TOKEN_HASH = 'communio_id_token';
export const OPS_AUTH_RETURN_KEY = 'communio_ops_auth_return';
/** Dispatched after clearOpsAuthSession so useFirebaseUser drops the synthetic bounce user. */
export const OPS_AUTH_CLEARED_EVENT = 'communio-ops-auth-cleared';

const memory = new Map<string, string>();

/**
 * Set when #communio_id_token is consumed on this page load. LoginForm mounts while
 * logged-out chrome is still visible; without this flag it would re-bounce to apex
 * after AuthContext already parked the ops session (regression from b96af97).
 */
let hashIdTokenConsumedThisLoad = false;

/** True after consumeHashIdToken succeeds on this JS realm / page load. */
export function wasHashIdTokenConsumedThisLoad(): boolean {
  return hashIdTokenConsumedThisLoad;
}

/** Test helper — resets the page-load park flag. */
export function resetHashIdTokenConsumedForTests(): void {
  hashIdTokenConsumedThisLoad = false;
}

function setItem(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    memory.set(key, value);
  }
}

function getItem(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
}

function removeItem(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    memory.delete(key);
  }
}

export function persistOpsAuthSession(idToken: string, refreshToken?: string, uid?: string): void {
  setItem(ID_TOKEN_KEY, idToken);
  if (refreshToken) setItem(REFRESH_TOKEN_KEY, refreshToken);
  const resolved = uid || uidFromIdToken(idToken);
  if (resolved) setItem(UID_KEY, resolved);
}

export function readOpsIdToken(): string | null {
  return getItem(ID_TOKEN_KEY);
}

export function readOpsUid(): string | null {
  return getItem(UID_KEY);
}

export function clearOpsAuthSession(): void {
  removeItem(ID_TOKEN_KEY);
  removeItem(REFRESH_TOKEN_KEY);
  removeItem(UID_KEY);
  removeItem(OPS_AUTH_RETURN_KEY);
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new Event(OPS_AUTH_CLEARED_EVENT));
  }
}

export function uidFromIdToken(jwt: string): string | null {
  const parts = jwt.split('.');
  if (parts.length < 2) return null;
  try {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { sub?: string; user_id?: string };
    return payload.sub || payload.user_id || null;
  } catch {
    return null;
  }
}

/** Email claim from a Firebase ID token (ops bounce / OTP). */
export function emailFromIdToken(jwt: string): string | null {
  const parts = jwt.split('.');
  if (parts.length < 2) return null;
  try {
    const json = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { email?: string };
    const email = (payload.email || '').trim();
    return email.includes('@') ? email : null;
  } catch {
    return null;
  }
}

export function consumeHashIdToken(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash.replace(/^#/, '');
  const params = new URLSearchParams(hash);
  const token = params.get(ID_TOKEN_HASH);
  if (!token) return null;
  persistOpsAuthSession(token);
  hashIdTokenConsumedThisLoad = true;
  params.delete(ID_TOKEN_HASH);
  const next = params.toString();
  const url = `${window.location.pathname}${window.location.search}${next ? `#${next}` : ''}`;
  window.history.replaceState(null, '', url);
  return token;
}

export function rememberOpsAuthReturn(url: string): void {
  if (typeof window === 'undefined') return;
  const normalized = normalizeOpsAuthReturn(url) || authReturnWithoutHash(url);
  setItem(OPS_AUTH_RETURN_KEY, normalized);
}

export function consumeOpsAuthReturn(): string | null {
  if (typeof window === 'undefined') return null;
  const v = getItem(OPS_AUTH_RETURN_KEY);
  removeItem(OPS_AUTH_RETURN_KEY);
  return v;
}

function authReturnFromQuery(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const q = new URLSearchParams(window.location.search).get('authReturn');
    return q ? authReturnWithoutHash(q) : null;
  } catch {
    return null;
  }
}

/** Pending product/apex return URL (session or ?authReturn=). */
export function peekOpsAuthReturn(): string | null {
  const stored = getItem(OPS_AUTH_RETURN_KEY);
  if (stored) {
    const n = normalizeOpsAuthReturn(stored);
    if (n) return n;
  }
  const q = authReturnFromQuery();
  if (q) {
    const n = normalizeOpsAuthReturn(q);
    if (n) return n;
  }
  return null;
}

/** After ops Google/OTP, send the ID token back to the product slug or apex start. */
export async function bounceIfOpsAuthReturn(idToken?: string | null): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const bounce = peekOpsAuthReturn();
  if (!bounce) return false;
  const token = (idToken || readOpsIdToken() || '').trim();
  if (!token) return false;
  removeItem(OPS_AUTH_RETURN_KEY);
  try {
    const dest = new URL(authReturnWithoutHash(bounce));
    dest.hash = `${ID_TOKEN_HASH}=${encodeURIComponent(token)}`;
    window.location.assign(dest.toString());
    return true;
  } catch {
    return false;
  }
}

/**
 * Absolute slug URL that started an apex auth bounce (never apex itself).
 * Apex is a transient sign-in host — cancel/fail must return here.
 */
export function peekSlugAuthOrigin(): string | null {
  const bounce = peekOpsAuthReturn();
  if (!bounce) return null;
  try {
    const u = new URL(bounce);
    if (!isCommunioProductSlugHost(u.hostname)) return null;
    return authReturnWithoutHash(u.toString());
  } catch {
    return null;
  }
}

/**
 * Abort apex auth and hard-navigate back to the originating slug (no ID token).
 * No-op when not on apex or when auth was started on apex (no slug authReturn).
 */
export function returnToSlugAuthOrigin(opts?: { error?: string }): boolean {
  if (typeof window === 'undefined') return false;
  if (!isCommunioApexHost(window.location.hostname)) return false;
  const origin = peekSlugAuthOrigin();
  if (!origin) return false;
  removeItem(OPS_AUTH_RETURN_KEY);
  try {
    const dest = new URL(origin);
    const err = (opts?.error || '').trim();
    if (err) {
      dest.searchParams.set('loginError', err.slice(0, 200));
    }
    window.location.assign(dest.toString());
    return true;
  } catch {
    return false;
  }
}

export async function exchangeCustomTokenRest(apiKey: string, customToken: string): Promise<{
  idToken: string;
  refreshToken: string;
  localId: string;
}> {
  const res = await fetch(
    `https://identitytoolkit.googleapis.com/v1/accounts:signInWithCustomToken?key=${encodeURIComponent(apiKey)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: customToken, returnSecureToken: true }),
    },
  );
  const data = (await res.json()) as {
    idToken?: string;
    refreshToken?: string;
    localId?: string;
    error?: { message?: string };
  };
  // Identity Toolkit often omits localId on VerifyCustomTokenResponse — uid is in the JWT.
  const localId = (data.localId || (data.idToken ? uidFromIdToken(data.idToken) : null) || '').trim();
  if (!res.ok || !data.idToken || !localId) {
    throw new Error(data.error?.message || 'custom token exchange failed');
  }
  persistOpsAuthSession(data.idToken, data.refreshToken, localId);
  return { idToken: data.idToken, refreshToken: data.refreshToken ?? '', localId };
}
